import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import type { BoqReprice } from "./boq";
import { ApiError, del, downloadFile, get, patch, post, postForm, put, withQuery } from "./client";

/** `/api/v1/quotations`, as `prv.web.api.quotation_views` shapes it. */

export type QuotationStatus =
  | "draft"
  | "submitted"
  | "under_review"
  | "returned"
  | "resubmitted"
  | "approved"
  | "rejected"
  | "superseded";

export interface Supplier {
  id: number;
  acc_no: string;
  name: string;
  discount_pct: string | null;
  labour_rate: string | null;
  km_rate: string | null;
  vatable: boolean;
}

export interface SiteRef {
  id: number;
  name: string;
  address: string | null;
}

/** The work queue a job sits in, computed on the server from the quotation
 * and every document raised from it (`prv.quotations.queue`). */
export type Queue = "approve" | "price" | "capture" | "bill" | "supplier" | "done";

/** A filter of the register: one queue, `action` for every queue but done,
 * or nothing for every job. */
export type Stage = Queue | "action";

export type QueueCounts = Record<Queue | "action" | "all", number>;

/** The supplier order as the job's pipeline shows it. */
export interface JobOrderRef {
  id: number;
  number: string;
  status: string;
  unpriced: number;
  po_date: string | null;
  issued_at: string | null;
  sent_at: string | null;
  acknowledged_at: string | null;
}

export interface JobBoqRef {
  id: number;
  name: string;
  status: string;
  boq_date: string | null;
  approved_at: string | null;
  issued_at: string | null;
}

/** Step 1: the office's draft BOQ, priced by hand before the supplier quoted,
 * found by the job's site and works order. `copied` once the customer BOQ was
 * raised from it, which freezes it. */
export interface JobDraftBoqRef {
  id: number;
  name: string;
  created_at: string | null;
  line_count: number;
  total_excl_vat: string;
  copied: boolean;
}

export interface JobCustomerOrderRef {
  id: number;
  number: string;
  status: string;
  order_date: string | null;
}

export interface JobInvoiceRef {
  id: number;
  number: string;
  invoice_date: string | null;
}

export interface QuotationRow {
  id: number;
  number: string;
  status: QuotationStatus;
  version: number;
  supplier: Supplier | null;
  site: SiteRef | null;
  inspection_id: number;
  work_order_no: string | null;
  quote_date: string | null;
  valid_until: string | null;
  total_excl_vat: string;
  expired: boolean;
  has_attachment: boolean;
  queue: Queue;
  /** One line on each document raised from the quotation. The City's three
   * are always null for a supplier, who is not shown them. */
  order: JobOrderRef | null;
  draft_boq: JobDraftBoqRef | null;
  boq: JobBoqRef | null;
  customer_order: JobCustomerOrderRef | null;
  invoice: JobInvoiceRef | null;
}

export interface Eligible {
  inspection_id: number;
  work_order_no: string;
  site: string;
  submitted_at: string | null;
}

export interface QuotationList {
  rows: QuotationRow[];
  eligible: Eligible[];
  /** For the whole search, whatever the stage or the page. */
  counts: QueueCounts;
  /** How many rows the stage holds across every page. */
  total: number;
  /** Pass back as `cursor` for the next page; null on the last one. */
  next_cursor: number | null;
}

export interface QuotationLine {
  id: number;
  seq: number;
  item_code: string | null;
  description: string;
  size: string | null;
  face_to_face: string | null;
  pressure_nominal: string | null;
  delivery: string | null;
  qty: string;
  unit_price: string;
  discount_pct: string;
  amount: string;
  spare_part_id: number | null;
  /** Which part of the standard service kit put this line on (`standard`
   * or `pilot`); null for a line the office keyed, which a kit switch keeps. */
  kit_role: "standard" | "pilot" | null;
}

export interface Foot {
  subtotal: string;
  discount_amount: string;
  total_excl: string;
  vat_rate: string;
  vat: string;
  total_incl: string;
}

export interface HistoryRow {
  version: number;
  at: string | null;
  by: string | null;
  status: QuotationStatus;
  note: string | null;
  total_excl_vat: string;
}

export interface Override {
  line_id: number;
  field: string;
  calculated: string | null;
  revised: string;
  reason: string | null;
  by: string | null;
  at: string | null;
}

export interface Mileage {
  planned_km: string | null;
  actual_km: string | null;
  tolerance: string | null;
  difference_km: string | null;
  fraction: string | null;
  reason: string | null;
  recorded_at: string | null;
  charge_km: string | null;
}

export interface QuotationDetail {
  id: number;
  number: string;
  status: QuotationStatus;
  version: number;
  quote_date: string | null;
  valid_until: string | null;
  terms: string | null;
  discount_pct: string | null;
  expired: boolean;
  has_attachment: boolean;
  captured_at: string | null;
  supplier: Supplier | null;
  site: SiteRef | null;
  inspection: {
    id: number;
    work_order_no: string;
    chamber_name: string | null;
    status: string;
    site_id: number | null;
  } | null;
  lines: QuotationLine[];
  foot: Foot;
  uncovered: { id: number; code: string | null; description: string; size: string }[];
  decision: { by: string | null; at: string | null; note: string | null } | null;
  supplier_order: JobOrderRef | null;
  /** Step 1, the office's draft BOQ; null for a supplier and a job with none. */
  draft_boq: JobDraftBoqRef | null;
  /** The same ref the register carries; this type declares what the screens use. */
  boq: JobBoqRef | null;
  /** The City's order against the bill and our invoice; never sent to a supplier. */
  customer_order: JobCustomerOrderRef | null;
  invoice: JobInvoiceRef | null;
  /** What approving this would do to the job's pre-issued BOQ. Office only;
   * null for a supplier and for a job with no pre-issued bill. */
  pre_issued_boq: BoqReprice | null;
  queue: Queue;
  boq_blocker: string | null;
  /** The office may delete this quotation (a false start: read twice, wrong site). */
  can_delete: boolean;
  /** Why it cannot be deleted yet, naming the step that comes first. */
  delete_blocker: string | null;
  letterhead_gaps: string[];
  fonts_available: boolean;
  standard_lines: {
    labour_rate: string | null;
    km_rate: string | null;
    km: string | null;
    has_labour: boolean;
    has_travel: boolean;
  };
  mileage: Mileage;
  history: HistoryRow[];
  overrides: Override[];
  actions: { can_edit: boolean; transitions: QuotationStatus[]; office: boolean };
  pdf_url: string;
  /** The standard service kit the quotation started with, and what the kit
   * could not settle on its own (a pilot model not recorded, no kit for the
   * size). All null for a supplier whose price list carries no kits. */
  kit: { size_label: string | null; pilot: string | null; note: string | null };
  /** What the Standard service dialog offers: the sizes on the price list in
   * force, and the pilot models its kits name. */
  kits: { sizes: { size_label: string; dn_mm: number; series: string | null }[]; pilots: string[] };
}

/** What a kit switch answers with: the fresh detail, plus the codes the kit
 * wanted but did not add. A bare code was already on a line the office keyed,
 * which the switch keeps at that line's own quantity and price; a code the
 * catalogue in force no longer carries says so after it. */
export interface KitSwitchDetail extends QuotationDetail {
  kit_result: { added: number; skipped: string[] };
}

export interface LineInput {
  description: string;
  qty: string;
  unit_price: string;
  discount_pct?: string | null;
  item_code?: string | null;
  size?: string | null;
  face_to_face?: string | null;
  pressure_nominal?: string | null;
  delivery?: string | null;
  reason?: string;
}

/** A line added to the quote: a part off the supplier's price list, named by
 * `spare_part_id` and priced from it by the server, or one typed by hand. */
export type NewLineInput =
  | { spare_part_id: number; qty: string; discount_pct?: string | null }
  | (LineInput & { spare_part_id?: null });

/** One part on the quotation's supplier's price list. */
export interface PriceListPart {
  spare_part_id: number;
  code: string;
  description: string;
  size: string;
  /** What the supplier's document prints; null when nobody has priced it. */
  list_price: string | null;
}

export type Move = "submit" | "resubmit" | "approve" | "reject" | "return";

/** What an approval did to the job's pre-issued BOQ. */
export interface BoqRepriceOutcome {
  boq_id: number | null;
  outcome: "applied" | "needs_reason" | "locked" | "unchanged" | "none";
  k1_before: string | null;
  k1_after: string | null;
  message: string;
}

/** The quotation after a move; an approval also says what it did to the bill. */
export interface MovedDetail extends QuotationDetail {
  boq_reprice?: BoqRepriceOutcome;
}

export const LIST_KEY = ["quotations"] as const;
export const detailKey = (id: number) => ["quotations", id] as const;

export const PAGE_SIZE = 50;

export interface RegisterQuery {
  search?: string;
  stage?: Stage | null;
  cursor?: number | null;
  limit?: number;
  order?: "newest" | "oldest";
}

/** The register's address for one page. The server filters, counts and pages
 * (Fix #23): the browser never holds more of the register than it shows. */
export function registerUrl({ search, stage, cursor, limit = PAGE_SIZE, order }: RegisterQuery): string {
  return withQuery("/api/v1/quotations", {
    search: search?.trim() || undefined,
    stage: stage ?? undefined,
    cursor: cursor ?? undefined,
    limit,
    order: order === "oldest" ? "oldest" : undefined,
  });
}

/** The first page of the register for a search, with every count. */
export function useQuotations(search: string) {
  return useQuery<QuotationList, ApiError>({
    queryKey: [...LIST_KEY, "page", { search }],
    queryFn: () => get<QuotationList>(registerUrl({ search })),
  });
}

/** The counts beside every filter, and the inspections waiting for a
 * quotation, without any rows (`limit=0`). */
export function useRegisterCounts(search: string) {
  return useQuery<QuotationList, ApiError>({
    queryKey: [...LIST_KEY, "counts", { search }],
    queryFn: () => get<QuotationList>(registerUrl({ search, limit: 0 })),
    placeholderData: keepPreviousData,
  });
}

/** The first few rows of one queue, for its section in the work queue. Asked
 * only while the section is open. */
export function useQueuePreview(stage: Queue, search: string, enabled: boolean, limit = 3) {
  return useQuery<QuotationList, ApiError>({
    queryKey: [...LIST_KEY, "preview", { stage, search, limit }],
    queryFn: () => get<QuotationList>(registerUrl({ search, stage, limit })),
    enabled,
  });
}

/** A flat list that grows fifty rows at a time: a queue shown in full, every
 * job, the done ones, or a search. */
export function useRegisterList(stage: Stage | null, search: string, order: "newest" | "oldest", enabled = true) {
  return useInfiniteQuery<QuotationList, ApiError>({
    queryKey: [...LIST_KEY, "list", { stage, search, order }],
    queryFn: ({ pageParam }) =>
      get<QuotationList>(registerUrl({ search, stage, order, cursor: pageParam as number | null })),
    initialPageParam: null,
    getNextPageParam: (last) => last.next_cursor,
    enabled,
  });
}

/** One page of the full-screen register, chosen by its cursor. */
export function useRegisterPage(stage: Stage | null, search: string, cursor: number | null, enabled = true) {
  return useQuery<QuotationList, ApiError>({
    queryKey: [...LIST_KEY, "full", { stage, search, cursor }],
    queryFn: () => get<QuotationList>(registerUrl({ search, stage, cursor })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useQuotation(id: number | null) {
  return useQuery<QuotationDetail, ApiError>({
    queryKey: detailKey(id ?? 0),
    queryFn: () => get<QuotationDetail>(`/api/v1/quotations/${id}`),
    enabled: id !== null,
  });
}

export function useSuppliers() {
  return useQuery<{ rows: Supplier[] }, ApiError>({
    queryKey: ["suppliers"],
    queryFn: () => get<{ rows: Supplier[] }>("/api/v1/suppliers"),
    staleTime: 5 * 60_000,
  });
}

/** Every write answers with the fresh detail, which is put straight into the
 * cache; the register is invalidated so its row catches up, and so is any other
 * screen the write changed (`alsoStale`). */
function useDetailWrite<TVars, TDetail extends QuotationDetail = QuotationDetail>(
  write: (vars: TVars) => Promise<TDetail>,
  alsoStale: readonly QueryKey[] = [],
) {
  const client = useQueryClient();
  return useMutation<TDetail, ApiError, TVars>({
    mutationFn: write,
    onSuccess: (detail) => {
      client.setQueryData(detailKey(detail.id), detail);
      void client.invalidateQueries({ queryKey: LIST_KEY });
      for (const queryKey of alsoStale) void client.invalidateQueries({ queryKey });
    },
  });
}

/** Approving reprices the site's pre-issued bill in the same request, so the
 * bill and the site drawer showing it are stale too. */
const BILL_SCREENS: readonly QueryKey[] = [["boq"], ["sites"]];

/** A quotation captured by hand names exactly one of the two: the job it
 * prices, or the site it is for (the server joins or opens that site's job). */
export interface StartBody {
  inspection_id: number | null;
  site_id: number | null;
  supplier_id: number;
  number: string;
  quote_date: string | null;
  valid_until: string | null;
  /** False when captured by hand: the quotation starts with no lines. */
  prefill: boolean;
}

export function useStartQuotation() {
  return useDetailWrite((body: StartBody) => post<QuotationDetail>("/api/v1/quotations", body));
}

/** A site in the register under the work order number typed. */
export interface WorkOrderSite {
  id: number;
  name: string;
  cluster: string | null;
}

/** The sites under a work order number, matched exactly. */
export function useWorkOrderSites(number: string) {
  return useQuery<{ number: string; sites: WorkOrderSite[] }, ApiError>({
    queryKey: ["quotations", "work-order-sites", number],
    queryFn: () =>
      get<{ number: string; sites: WorkOrderSite[] }>(
        `/api/v1/quotations/work-order-sites?number=${encodeURIComponent(number)}`,
      ),
    enabled: number !== "",
  });
}

export function useUpdateHeader(id: number) {
  return useDetailWrite(
    (body: { number: string; quote_date: string | null; valid_until: string | null; terms: string | null }) =>
      patch<QuotationDetail>(`/api/v1/quotations/${id}`, body),
  );
}

export function useAddLine(id: number) {
  return useDetailWrite((body: NewLineInput) => post<QuotationDetail>(`/api/v1/quotations/${id}/lines`, body));
}

/** The quotation's supplier's price list, and their standing discount. */
export function usePriceList(id: number, enabled: boolean) {
  return useQuery<{ parts: PriceListPart[]; discount_pct: string }, ApiError>({
    queryKey: ["quotations", id, "catalogue"],
    queryFn: () => get(`/api/v1/quotations/${id}/catalogue`),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useEditLine(id: number) {
  return useDetailWrite((vars: { lineId: number; body: LineInput }) =>
    put<QuotationDetail>(`/api/v1/quotations/${id}/lines/${vars.lineId}`, vars.body),
  );
}

export function useDeleteLine(id: number) {
  return useDetailWrite((lineId: number) => del<QuotationDetail>(`/api/v1/quotations/${id}/lines/${lineId}`));
}

export function useStandardLines(id: number) {
  return useDetailWrite((hours: string) =>
    post<QuotationDetail>(`/api/v1/quotations/${id}/lines/standard`, { hours }),
  );
}

/** Switch a draft quotation to another size's service kit, with or without a
 * pilot kit. The kit's own lines are replaced; lines the office keyed stay,
 * and the answer's `kit_result` names the codes they kept from the kit. */
export function useKit(id: number) {
  return useDetailWrite((body: { size_label: string; pilot: string | null }) =>
    post<KitSwitchDetail>(`/api/v1/quotations/${id}/lines/kit`, body),
  );
}

export function useAttach(id: number) {
  return useDetailWrite((file: File) => {
    const form = new FormData();
    form.append("document", file, file.name);
    return postForm<QuotationDetail>(`/api/v1/quotations/${id}/attachment`, form);
  });
}

export function useMove(id: number) {
  return useDetailWrite(
    (vars: { move: Move; note?: string; boq_reason?: string }) =>
      post<MovedDetail>(
        `/api/v1/quotations/${id}/${vars.move}`,
        vars.move === "approve" ? { note: vars.note ?? null, boq_reason: vars.boq_reason ?? null } : { note: vars.note ?? null },
      ),
    BILL_SCREENS,
  );
}

/** Delete a quotation uploaded by mistake, with the unsent order and draft
 * bill that came from it. The reason goes on the site's history. */
export function useDeleteQuotation(id: number) {
  const client = useQueryClient();
  return useMutation<{ deleted: string }, ApiError, string>({
    mutationFn: (reason) => post<{ deleted: string }>(`/api/v1/quotations/${id}/delete`, { reason }),
    onSuccess: () => {
      client.removeQueries({ queryKey: detailKey(id) });
      void client.invalidateQueries({ queryKey: LIST_KEY });
      void client.invalidateQueries({ queryKey: ["purchase-orders"] });
      void client.invalidateQueries({ queryKey: ["boq"] });
      void client.invalidateQueries({ queryKey: ["sites"] });
    },
  });
}

export function useRecordMileage(id: number) {
  return useDetailWrite((body: { actual_km: string; reason: string }) =>
    post<QuotationDetail>(`/api/v1/quotations/${id}/mileage`, body),
  );
}

/** The supplier's order for an approved quotation that approval did not
 * order: lapsed prices need a reason, which is kept on the order. */
export function useRaiseSupplierOrder(id: number) {
  const client = useQueryClient();
  return useMutation<{ purchase_order_id: number; number: string }, ApiError, string>({
    mutationFn: (expiryAck) =>
      post<{ purchase_order_id: number; number: string }>(`/api/v1/quotations/${id}/order`, {
        expiry_ack: expiryAck.trim() === "" ? null : expiryAck.trim(),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: LIST_KEY });
      void client.invalidateQueries({ queryKey: ["purchase-orders"] });
    },
  });
}

/** The tender's own BOQ workbook for a bill, downloaded as a file. */
export function downloadBoqWorkbook(boqId: number): void {
  downloadFile(`/boq/${boqId}.xlsx`);
}

/** The quotation on the supplier's template with their standing discount left
 * off -- the full-price copy that goes to the City with the BOQ. */
export function fullPriceQuoteUrl(quotationId: number): string {
  return `/quotations/${quotationId}.pdf?price=full`;
}

export function useRaiseBoq(id: number) {
  const client = useQueryClient();
  return useMutation<{ boq_id: number; url: string }, ApiError>({
    mutationFn: () => post<{ boq_id: number; url: string }>(`/api/v1/quotations/${id}/boq`),
    onSuccess: (raised) => {
      // Raising the bill hands over its workbook, as the standalone PO
      // generator's BOQ tab does: a draft copy until the bill is approved.
      // And the full-price quote that goes to the City with it.
      downloadBoqWorkbook(raised.boq_id);
      downloadFile(fullPriceQuoteUrl(id));
      // The detail and the register: raising the bill moves the job's queue.
      void client.invalidateQueries({ queryKey: LIST_KEY });
      void client.invalidateQueries({ queryKey: ["boq"] });
    },
  });
}

export const STATUS_LABEL: Record<QuotationStatus, string> = {
  draft: "Draft",
  submitted: "Submitted",
  under_review: "Under review",
  returned: "Returned",
  resubmitted: "Resubmitted",
  approved: "Approved",
  rejected: "Rejected",
  superseded: "Superseded",
};

export const MOVE_LABEL: Record<Move, string> = {
  submit: "Submit for approval",
  resubmit: "Resubmit",
  approve: "Approve",
  reject: "Reject",
  return: "Return to supplier",
};

/** Which move reaches which status, so the buttons offered are exactly the
 * transitions the server allows from here. */
export const MOVE_TO: Record<Move, QuotationStatus> = {
  submit: "submitted",
  resubmit: "resubmitted",
  approve: "approved",
  reject: "rejected",
  return: "returned",
};

export function movesFor(detail: QuotationDetail): Move[] {
  const allowed = new Set(detail.actions.transitions);
  const office = detail.actions.office;
  return (Object.keys(MOVE_TO) as Move[]).filter((move) => {
    if (!allowed.has(MOVE_TO[move])) {
      return false;
    }
    if (!office && (move === "approve" || move === "reject" || move === "return")) {
      return false;
    }
    // Resubmit is the move from RETURNED; submit is the move from DRAFT. The
    // server names the destination, so a returned quotation offers resubmit,
    // never a second "submit".
    if (move === "submit" && detail.status === "returned") {
      return false;
    }
    if (move === "resubmit" && detail.status !== "returned") {
      return false;
    }
    return true;
  });
}
